import { useCallback, useEffect, useRef, useState } from 'react';

import { useConnectionState, useJoin } from 'agora-rtc-react';

import { AppContext, usePrepareAppState } from '@/app/AppContext';
import { APP_ID } from '@/app/config';
import { useAutoRecording, useRecording } from '@/app/mutations/RecordingMutation/context';
import { useAutoTranscription, useTranscription } from '@/app/mutations/TranscriptionMutation/context';
import useLocationBreadcrumbs from '@/app/location/useLocationBreadcrumbs';
import { useJoinData, useUserData } from '@/app/queries/MeetingQuery/context';
import useSoundEffects from '@/app/hooks/useSoundEffects';
import MeetingView from './MeetingView';
import { Permissions } from '../../types';


interface IMeetingPageProps {
  onEndCall(): void;
  permissions: Permissions
}

export default function MeetingPage({ onEndCall, permissions }: IMeetingPageProps) {
  const userData = useUserData();
  const appState = usePrepareAppState(permissions, userData.isHost);
  const { playJoinSound } = useSoundEffects();

  const joinData = useJoinData(APP_ID);

  useJoin(joinData, true);

  const recording = useRecording();
  const transcription = useTranscription();

  // Real RTC connection state drives the presence gate, read as "has not left"
  // rather than "is perfectly connected right now". A host RECONNECTING is a
  // network wobble, and at a 60s grace `useIsConnected()` (strictly CONNECTED)
  // would tear the recording down over one — losing real footage. Only a real
  // DISCONNECTED stops the feature. A one-sided start is still impossible: no
  // remote user is present until this client has actually connected.
  const joined = useConnectionState() !== 'DISCONNECTED';

  // Once the user deliberately ends the call we disable the gate so it cannot
  // restart while the feedback dialog is still open (both sides are still in the
  // channel at that point). The effect below sends the actual prompt stop.
  const [callEnding, setCallEnding] = useState(false);
  const recordingEnabled = userData.isHost && import.meta.env.VITE_ENABLE_RECORDING;

  useAutoRecording({ joined }, recordingEnabled && !callEnding);
  useAutoTranscription({ joined }, recordingEnabled && !callEnding);
  useLocationBreadcrumbs(userData.isClient);

  const handleBeforeEndCall = useCallback(() => {
    // Deliberate meeting end: disable the gate so it can't (re)start. The effect
    // below drives the actual prompt stop.
    setCallEnding(true);
  }, []);

  // On deliberate call end, force a prompt, non-rate-limited stop as soon as the
  // feature is active. If a start is still in flight when the host ends the call,
  // this fires the moment that start resolves (the active flag flips true) — so a
  // deliberate end never falls back to the server backstop, and it never sends a
  // spurious stop when nothing ever started (e.g. a customer no-show). The ref
  // makes each stop one-shot: the two features stop independently, so without it
  // one flag flipping would re-run this effect and re-send the other's stop.
  const endStopSentRef = useRef({ recording: false, transcription: false });
  useEffect(() => {
    if (!callEnding) return;
    if (recording.isRecording && !endStopSentRef.current.recording) {
      endStopSentRef.current.recording = true;
      recording.stop({ force: true });
    }
    if (transcription.isTranscribing && !endStopSentRef.current.transcription) {
      endStopSentRef.current.transcription = true;
      transcription.stop({ force: true });
    }
  }, [callEnding, recording, transcription]);

  useEffect(() => {
    playJoinSound();
  }, [playJoinSound]);

  return (
    <AppContext.Provider value={appState}>
      <MeetingView onEndCall={onEndCall} onBeforeEndCall={handleBeforeEndCall} />
    </AppContext.Provider>
  );
}
